import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'

// Lazy load all other views for better performance
const RegisterView = () => import('@/views/RegisterView.vue')
const LoginView = () => import('@/views/LoginView.vue')
const PageNotFound = () => import('@/views/PageNotFound.vue')
const MentionsView = () => import('@/views/MentionsView.vue')
const ChevalView = () => import('@/views/ChevalView.vue')
const DemandeSante = () => import('@/views/DemandeSante.vue')
const ChangerMotDePasse = () => import('@/views/ChangerMotDePasse.vue')
const AccountView = () => import('@/views/AccountView.vue')
const ProfileView = () => import('@/views/user/ProfileView.vue')
const MesCommandes = () => import('@/views/user/MesCommandes.vue')
const MesAbonnements = () => import('@/views/user/MesAbonnements.vue')
const MonPanier = () => import('@/views/user/MonPanier.vue')
const MoyenPaiement = () => import('@/views/user/MoyenPaiement.vue')
const MesAdresses = () => import('@/views/user/MesAdresses.vue')
const MesChevaux = () => import('@/views/user/MesChevaux.vue')
const VenteChevaux = () => import('@/views/user/VenteChevaux.vue')
const AjoutCheval = () => import('@/views/user/AjoutCheval.vue')
const TransfertCheval = () => import('@/views/user/TransfertCheval.vue')
const ChangerProprietaire = () => import('@/views/user/ChangerProprietaire.vue')
const NousContacter = () => import('@/views/user/NousContacter.vue')
const ConfirmationEmail = () => import('@/views/ConfirmationEmail.vue')
const AchatCheval = () => import('@/views/user/AchatCheval.vue')
const InscriptionPro = () => import('@/views/InscriptionPro.vue')
const Transferts = () => import('@/views/user/Transferts.vue')
const ConfirmationSante = () => import('@/views/ConfirmationSante.vue')
const RechercheCheval = () => import('@/views/veterinary/RechercheCheval.vue')
const ResetPassword = () => import('@/views/ResetPassword.vue')
const PreventeCheval = () => import('@/views/user/PreventeCheval.vue')
const SellAnimalView = () => import('@/views/user/SellAnimalView.vue')

// Pet views (lazy loaded)
const MesAnimaux = () => import('@/views/user/MesAnimaux.vue')
const AjoutPet = () => import('@/views/user/AjoutPet.vue')
const PetView = () => import('@/views/user/PetView.vue')
const VenteAnimaux = () => import('@/views/user/VenteAnimaux.vue')
const AchatAnimal = () => import('@/views/user/AchatAnimal.vue')
const RechercheAnimal = () => import('@/views/user/RechercheAnimal.vue')

// Admin imports (lazy loaded)
const AdminLayout = () => import('@/views/admin/AdminLayout.vue')
const AdminDashboard = () => import('@/views/admin/DashboardView.vue')
const AdminUsers = () => import('@/views/admin/UsersView.vue')
const AdminUserDetail = () => import('@/views/admin/UserDetailView.vue')
const AdminUserEdit = () => import('@/views/admin/UserEditView.vue')
const AdminHorses = () => import('@/views/admin/HorsesView.vue')
const AdminHorseDetail = () => import('@/views/admin/HorseDetailView.vue')
const AdminHorseEdit = () => import('@/views/admin/HorseEditView.vue')
const AdminAnimals = () => import('@/views/admin/AnimalsView.vue')
const AdminAnimalDetail = () => import('@/views/admin/AnimalDetailView.vue')
const AdminAnimalEdit = () => import('@/views/admin/AnimalEditView.vue')
const AdminOrders = () => import('@/views/admin/OrdersView.vue')
const AdminOrderDetail = () => import('@/views/admin/OrderDetailView.vue')
const AdminVetoRequests = () => import('@/views/admin/VetoRequestsView.vue')
const AdminTreatments = () => import('@/views/admin/TreatmentsView.vue')
const AdminListings = () => import('@/views/admin/ListingsView.vue')
const AdminAudit = () => import('@/views/admin/AuditView.vue')
const AdminPrintMonitor = () => import('@/views/admin/PrintMonitorView.vue')
const AdminEndpoints = () => import('@/views/admin/EndpointStatsView.vue')
const AdminPerformance = () => import('@/views/admin/PerformanceView.vue')
const AdminEngagement = () => import('@/views/admin/EngagementView.vue')
const AdminOperations = () => import('@/views/admin/OperationsView.vue')
const AdminFinance = () => import('@/views/admin/FinanceView.vue')

// Import Pinia auth store (Vue 3)
import { useAuthStore } from '@/stores/auth'

/**
 * Helper to get auth state from Pinia store
 */
function getAuthState() {
    // Try Pinia (primary approach in Vue 3)
    try {
        const authStore = useAuthStore();
        if (authStore.token) {
            return {
                token: authStore.token,
                role: authStore.role,
                is_pro: authStore.is_pro,
                id: authStore.id
            };
        }
    } catch (e) {
        // Pinia not ready yet, check localStorage
    }
    
    // Fall back to localStorage
    const storage = JSON.parse(localStorage.getItem('auth')) || {};
    return {
        token: storage.token || null,
        role: storage.role || null,
        is_pro: storage.is_pro,
        id: storage.id
    };
}

/**
 * Decode JWT token and get payload
 */
function decodeToken(token) {
    try {
        const payload = token.split('.')[1];
        return JSON.parse(atob(payload));
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
}

// Roles that have professional (veto-like) access to the app
const PRO_ROLES = new Set(['Veto', 'Dentiste Équin', 'Ostéopathe', 'Maréchal-Ferrant']);

function isProRole(role) {
    return PRO_ROLES.has(role);
}

function guardMyroute(to, from, next) {
    const auth = getAuthState();
    const isAuthenticated = !!auth.token;
    
    if (isAuthenticated) {
        const decoded = decodeToken(auth.token);
        
        // Block admins from regular app
        if (decoded && decoded.is_admin === true) {
            next('/admin');
            return;
        }
        
        if (to.meta.role) {
            if (to.meta.role === auth.role) {
                next();
            } else {
                if (isProRole(auth.role)) {
                    next('/recherche-cheval');
                } else {
                    next('/mon-compte');
                }
            }
        } else {
            next();
        }
    } else {
        next('/connexion');
    }
}

function guardVetoRoute(to, from, next) {
    const auth = getAuthState();
    const isAuthenticated = !!auth.token;
    
    if (isAuthenticated) {
        const decoded = decodeToken(auth.token);
        
        if (decoded && decoded.is_admin === true) {
            next();
            return;
        }
        
        if (isProRole(auth.role)) {
            next();
            return;
        }
        
        next('/mon-compte');
    } else {
        next('/connexion');
    }
}

function guardAdminRoute(to, from, next) {
    const auth = getAuthState();
    const isAuthenticated = !!auth.token;
    
    if (isAuthenticated) {
        const decoded = decodeToken(auth.token);
        
        if (decoded && decoded.is_admin === true) {
            next();
            return;
        }
        
        if (isProRole(auth.role)) {
            next('/recherche-cheval');
        } else {
            next('/mon-compte');
        }
    } else {
        next('/connexion');
    }
}

function noHomePage(to, from, next) {
    const auth = getAuthState();
    
    if (auth.role !== null) {
        const decoded = decodeToken(auth.token);
        
        // Redirect admins to their dashboard
        if (decoded && decoded.is_admin === true) {
            next('/admin');
            return;
        }
        
        next('/mon-compte');
    } else {
        next();
    }
}

const routes = [
    //  Page d'accueil
    {
        path: '/',
        name: 'home',
        meta: {role: 'Visitor'},
        component: HomeView,
        beforeEnter: noHomePage
    },
    //  Page d'inscription
    {
        path: '/inscription',
        name: 'RegisterView',
        meta: {role: 'Visitor'},
        component: RegisterView
    },
    //  Page d'inscription professionel
    {
        path: '/inscription-pro',
        name: 'InscriptionPro',
        component: InscriptionPro
    },
    //  Page de connexion
    {
        path: '/connexion',
        name: 'LoginView',
        meta: {role: 'Visitor'},
        component: LoginView
    },
    {
        path: '/confirmation_email',
        name: 'ConfirmationEmail',
        meta: {role: 'Visitor'},
        component: ConfirmationEmail
    },
    // Mon compte
    {
        path: '/mon-compte',
        name: 'AccountView',
        component: AccountView,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Profil de l'utilisateur
    {
        path: '/mon-profil',
        name: 'ProfileView',
        component: ProfileView,
        beforeEnter: guardMyroute
    },
    //  Chevaux
    {
        path: '/mes-chevaux',
        name: 'MesChevaux',
        component: MesChevaux,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Commandes
    {
        path: '/mes-commandes',
        name: 'MesCommandes',
        component: MesCommandes,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Abonnements
    {
        path: '/mes-abonnements',
        name: 'MesAbonnements',
        component: MesAbonnements,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Panier
    {
        path: '/mon-panier',
        name: 'MonPanier',
        component: MonPanier,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Moyens de paiement
    {
        path: '/moyens-paiement',
        name: 'MoyenPaiement',
        component: MoyenPaiement,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Adresses
    {
        path: '/mes-adresses',
        name: 'MesAdresses',
        component: MesAdresses,
        beforeEnter: guardMyroute
    },
    //  Ajouter un cheval
    {
        path: '/ajouter-un-cheval',
        name: 'AjoutCheval',
        component: AjoutCheval,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    // Transferts
    {
        path: '/transfert-de-chevaux',
        name: 'TransfertCheval',
        component: TransfertCheval,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    {
        path: '/transferts',
        name: 'TransfertsChevaux',
        component: Transferts,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Transferts des chevaux
    {
        path: '/changement-de-proprietaire',
        name: 'ChangementProprietaire',
        component: ChangerProprietaire,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Acheter un cheval
    {
        path: '/achat-cheval/:id',
        name: 'AchatCheval',
        component: AchatCheval,
    },
    //  Chevaux en vente
    {
        path: '/chevaux-en-vente',
        name: 'VenteChevaux',
        component: VenteChevaux,
    },
    // Mettre en vente cheval
    {
        path: '/mettre-en-vente/:id',
        name: 'Prevente Cheval',
        component: PreventeCheval,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    // Vendre un animal (centralized sell page)
    {
        path: '/vendre-un-animal',
        name: 'SellAnimal',
        component: SellAnimalView,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Changement de mot de passe
    {
        path: '/changement-mot-de-passe',
        name: 'ChangerMotDePasse',
        component: ChangerMotDePasse,
        meta: {role: 'Default'},
    },
    //  Demande d'un compte professionel de santé
    {
        path: '/demande-pro-sante',
        name: 'DemandeSante',
        component: DemandeSante
    },
    {
        path: '/confirmation-pro-sante',
        name: 'ConfirmationSante',
        component: ConfirmationSante
    },
    //  Mentions légales
    {
        path: '/mentions-legales',
        name: 'Mentions',
        component: MentionsView
    },
    {
        path: '/cheval/:id',
        name: 'ChevalView',
        component: ChevalView,
    },
    {
        path: '/recherche-cheval',
        name: 'RechercheCheval',
        component: RechercheCheval,
        meta: { role: 'Veto' },
        beforeEnter: guardVetoRoute,
    },
    {
        path: '/nous-contacter',
        name: 'NousContacter',
        component: NousContacter,
    },
    {
        path: '/reset_password/:token',
        name: 'ResetPassword',
        component: ResetPassword,
        meta: {role: 'Default'},
    },
    // ============= PET ROUTES =============
    //  Mes animaux (all pets)
    {
        path: '/mes-animaux',
        name: 'MesAnimaux',
        component: MesAnimaux,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Ajouter un animal
    {
        path: '/ajouter-un-animal',
        name: 'AjoutPet',
        component: AjoutPet,
        meta: {role: 'Default'},
        beforeEnter: guardMyroute
    },
    //  Vue détaillée d'un animal
    {
        path: '/animal/:id',
        name: 'PetView',
        component: PetView,
    },
    //  Animaux en vente
    {
        path: '/animaux-en-vente',
        name: 'VenteAnimaux',
        component: VenteAnimaux,
    },
    //  Acheter un animal
    {
        path: '/achat-animal/:id',
        name: 'AchatAnimal',
        component: AchatAnimal,
    },
    //  Recherche animal (veto)
    {
        path: '/recherche-animal',
        name: 'RechercheAnimal',
        component: RechercheAnimal,
        meta: { role: 'Veto' },
        beforeEnter: guardVetoRoute,
    },
    // ============= ADMIN ROUTES =============
    {
        path: '/admin',
        component: AdminLayout,
        meta: { requiresAdmin: true },
        beforeEnter: guardAdminRoute,
        children: [
            {
                path: '',
                name: 'AdminDashboard',
                component: AdminDashboard
            },
            {
                path: 'users',
                name: 'AdminUsers',
                component: AdminUsers
            },
            {
                path: 'users/:id',
                name: 'AdminUserDetail',
                component: AdminUserDetail
            },
            {
                path: 'users/:id/edit',
                name: 'AdminUserEdit',
                component: AdminUserEdit
            },
            {
                path: 'horses',
                name: 'AdminHorses',
                component: AdminHorses
            },
            {
                path: 'horses/:uenl',
                name: 'AdminHorseDetail',
                component: AdminHorseDetail
            },
            {
                path: 'horses/:uenl/edit',
                name: 'AdminHorseEdit',
                component: AdminHorseEdit
            },
            {
                path: 'animals',
                name: 'AdminAnimals',
                component: AdminAnimals
            },
            {
                path: 'animals/:code',
                name: 'AdminAnimalDetail',
                component: AdminAnimalDetail
            },
            {
                path: 'animals/:code/edit',
                name: 'AdminAnimalEdit',
                component: AdminAnimalEdit
            },
            {
                path: 'orders',
                name: 'AdminOrders',
                component: AdminOrders
            },
            {
                path: 'orders/:id',
                name: 'AdminOrderDetail',
                component: AdminOrderDetail
            },
            {
                path: 'veto-requests',
                name: 'AdminVetoRequests',
                component: AdminVetoRequests
            },
            {
                path: 'treatments',
                name: 'AdminTreatments',
                component: AdminTreatments
            },
            {
                path: 'listings',
                name: 'AdminListings',
                component: AdminListings
            },
            {
                path: 'audit',
                name: 'AdminAudit',
                component: AdminAudit
            },
            {
                path: 'print-monitor',
                name: 'AdminPrintMonitor',
                component: AdminPrintMonitor
            },
            {
                path: 'endpoints',
                name: 'AdminEndpoints',
                component: AdminEndpoints
            },
            {
                path: 'performance',
                name: 'AdminPerformance',
                component: AdminPerformance
            },
            {
                path: 'engagement',
                name: 'AdminEngagement',
                component: AdminEngagement
            },
            {
                path: 'operations',
                name: 'AdminOperations',
                component: AdminOperations
            },
            {
                path: 'finance',
                name: 'AdminFinance',
                component: AdminFinance
            }
        ]
    },
    //  Page 404 (Vue Router 4 syntax)
    {
        path: '/:pathMatch(.*)*',
        name: 'PageNotFound',
        component: PageNotFound
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior() {
        document.getElementById('app').scrollIntoView({ behavior: 'smooth' });
    }
})

// Global navigation guard: check token expiry and attempt refresh
router.beforeEach(async (to, from, next) => {
    // Skip token checks for public pages
    const publicPages = ['home', 'RegisterView', 'LoginView', 'PageNotFound', 'MentionsView', 'ConfirmationEmail', 'ResetPassword', 'InscriptionPro', 'ConfirmationSante', 'DemandeSante'];
    if (publicPages.includes(to.name)) {
        next();
        return;
    }
    
    try {
        const authStore = useAuthStore();
        
        // If we have auth state but no access token (page reload), try to restore session
        if (!authStore.token && (authStore.role || authStore.id)) {
            const restored = await authStore.restoreSession();
            if (!restored) {
                next('/connexion');
                return;
            }
        }
        
        // If token is expired, attempt a refresh before proceeding
        if (authStore.token && authStore.isTokenExpired()) {
            const refreshed = await authStore.refreshAccessToken();
            if (!refreshed) {
                authStore.clearState();
                next('/connexion');
                return;
            }
        }
    } catch (e) {
        // If anything fails during auth check, just continue
        console.warn('Auth check error:', e);
    }
    
    next();
})

export default router
